import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  TUseMutationContext,
  TUseMutationOptions,
  TUseQueryOptions,
} from '@uniqkey-frontend/shared-app';
import {
  DeleteBulkGroupRequest,
  DeleteBulkGroupResponse,
  GetGroupByIdResponse,
  NoContentResult,
  Operation,
} from '@uniqkey-backend-organization-web/api-client';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import { IReplacePatchOperation, parseReplacePatchOperation } from '../../../helpers/apiClients';
import useEmployeeGroupsAPI from '../../useEmployeeGroupsAPI';

interface IUseGetGroupByIdParams {
  groupId: string;
}
export const useGetGroupById = (
  params: IUseGetGroupByIdParams,
  options: TUseQueryOptions<GetGroupByIdResponse> = {},
) => {
  const { groupId } = params;
  const { getGroupById } = useEmployeeGroupsAPI();
  return useQuery<GetGroupByIdResponse>(
    [ReactQueryKeyEnum.Group, groupId],
    ({ signal }) => getGroupById(groupId, { signal }),
    { notifyOnChangeProps: 'tracked', ...options },
  );
};

interface IUsePatchGroupByIdParams {
  groupId: string;
  useOptimisticUpdates?: boolean;
}
export const usePatchGroupById = (
  params: IUsePatchGroupByIdParams,
  options: TUseMutationOptions<
    NoContentResult,
    unknown,
    Operation[],
    TUseMutationContext<GetGroupByIdResponse>
  > = {},
) => {
  const { groupId, useOptimisticUpdates = false } = params;
  const queryClient = useQueryClient();
  const { patchGroupById } = useEmployeeGroupsAPI();
  const mutationKey = [ReactQueryKeyEnum.Group, groupId];
  return useMutation(mutationKey, (operations) => patchGroupById(groupId, operations), {
    onMutate: async (operations) => {
      if (!useOptimisticUpdates) {
        return null;
      }
      const updatedParts = operations.reduce((acc, operation) => ({
        ...acc,
        ...parseReplacePatchOperation(operation as IReplacePatchOperation),
      }), {});
      await queryClient.cancelQueries(mutationKey);
      const previousValue = queryClient.getQueryData<GetGroupByIdResponse>(mutationKey);
      queryClient.setQueryData<GetGroupByIdResponse>(
        mutationKey,
        (oldGroup) => ({
          ...oldGroup,
          ...updatedParts as GetGroupByIdResponse,
        }),
      );
      return { previousValue: previousValue as GetGroupByIdResponse };
    },
    onError: (err, group, context) => {
      if (context?.previousValue) {
        queryClient.setQueryData<GetGroupByIdResponse>(mutationKey, context.previousValue);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(mutationKey);
    },
    ...options,
  });
};

export const useDeleteGroups = (
  options: TUseMutationOptions<DeleteBulkGroupResponse, unknown, DeleteBulkGroupRequest, void> = {},
) => {
  const queryClient = useQueryClient();
  const { deleteGroups } = useEmployeeGroupsAPI();
  return useMutation((deleteBulkGroupRequest) => deleteGroups(deleteBulkGroupRequest), {
    onSettled: (data, error, variables) => {
      variables.groupIds.forEach((groupId) => {
        queryClient.removeQueries([ReactQueryKeyEnum.Group, groupId], { exact: true });
      });
    },
    ...options,
  });
};
